<template>
  <div class="show_all_content_wrapper">
    <!-- Start:: Main Section -->
    <main>
      <!--  =========== Start:: Filter Form =========== -->
      <div
        class="filter_content_wrapper"
        :class="{ active: filterFormIsActive }"
      >
        <button
          class="filter_toggler"
          @click="filterFormIsActive = !filterFormIsActive"
        >
          <i class="fal fa-times"></i>
        </button>
        <div class="filter_title_wrapper">
          <h5>{{ $t("TITLES.searchBy") }}</h5>
        </div>
        <div class="filter_form_wrapper">
          <form @submit.prevent="submitFilterForm">
            <div class="row justify-content-center align-items-center w-100">
              <!-- Start:: Name Input -->
              <base-input
                col="3"
                type="text"
                :placeholder="$t('PLACEHOLDERS.full_name')"
                v-model.trim="filterOptions.name"
              />
              <!-- End:: Name Input -->

              <!-- Start:: Phone Input -->
              <base-input
                col="3"
                type="tel"
                :placeholder="$t('PLACEHOLDERS.phone')"
                v-model.trim="filterOptions.phone"
              />
              <!-- End:: Phone Input -->

              <!-- Start:: Subscription Status Filter -->
              <base-select-input
                col="3"
                :optionsList="subscriptionStatuses"
                :placeholder="$t('PLACEHOLDERS.subscription_status')"
                v-model="filterOptions.status"
              />
              <!-- End:: Subscription Status Filter -->

              <!-- Start:: Start Date Filter -->
              <base-input
                col="3"
                type="date"
                :placeholder="$t('PLACEHOLDERS.subscription_start_date')"
                v-model="filterOptions.start_date"
              />
              <!-- End:: Start Date Filter -->

              <!-- Start:: End Date Filter -->
              <base-input
                col="3"
                type="date"
                :placeholder="$t('PLACEHOLDERS.subscription_end_date')"
                v-model="filterOptions.end_date"
              />
              <!-- End:: End Date Filter -->
            </div>

            <div class="btns_wrapper">
              <button class="submit_btn" :disabled="isWaitingRequest">
                <i class="fal fa-search"></i>
              </button>
              <button
                class="reset_btn"
                type="button"
                :disabled="isWaitingRequest"
                @click="resetFilter"
              >
                <i class="fal fa-redo"></i>
              </button>
            </div>
          </form>
        </div>
      </div>
      <!--  =========== End:: Filter Form =========== -->

      <!--  =========== Start:: Table Title =========== -->
      <div class="table_title_wrapper">
        <div class="title_text_wrapper">
          <h5>{{ $t("TITLES.user_subscriptions") }}</h5>
          <button
            v-if="!filterFormIsActive"
            class="filter_toggler"
            @click.stop="filterFormIsActive = !filterFormIsActive"
          >
            <i class="fal fa-search"></i>
          </button>
        </div>
      </div>
      <!--  =========== End:: Table Title =========== -->

      <!--  =========== Start:: Data Table =========== -->
      <v-data-table
        class="thumb"
        :loading="loading"
        :loading-text="$t('TABLES.loadingData')"
        :search="searchValue"
        :headers="tableHeaders"
        :items="tableRows"
        item-class="ltr"
        :items-per-page="paginations.items_per_page"
        hide-default-footer
      >
        <!-- Start:: No Data State -->
        <template v-slot:no-data>
          {{ $t("TABLES.noData") }}
        </template>
        <!-- End:: No Data State -->

        <!-- Start:: Serial Number -->
        <template v-slot:[`item.id`]="{ item, index }">
          <span>
            {{
              (paginations.current_page - 1) * paginations.items_per_page +
              index +
              1
            }}
          </span>
        </template>
        <!-- End:: Serial Number -->

        <!-- Start:: Subscription Status -->
        <template v-slot:[`item.status`]="{ item }">
          <v-chip :color="getStatusColor(item.status)" text-color="white" small>
            {{ getStatusText(item.status) }}
          </v-chip>
        </template>
        <!-- End:: Subscription Status -->

        <!-- Start:: Remaining Days -->
        <template v-slot:[`item.remaining_days`]="{ item }">
          <span :class="getRemainingDaysClass(item.remaining_days)">
            {{ item.remaining_days }} {{ $t("PLACEHOLDERS.days") }}
          </span>
        </template>
        <!-- End:: Remaining Days -->

        <!-- Start:: Price -->
        <template v-slot:[`item.price`]="{ item }">
          <span>{{ item.price }} {{ $t("PLACEHOLDERS.sar") }}</span>
        </template>
        <!-- End:: Price -->

        <!-- Start:: Actions -->
        <template v-slot:[`item.actions`]="{ item }">
          <div class="actions">
            <!-- View Details -->
            <a-tooltip placement="bottom">
              <template slot="title">
                <span>{{ $t("BUTTONS.show") }}</span>
              </template>
              <button class="btn_show" @click="showDetails(item)">
                <i class="fal fa-eye"></i>
              </button>
            </a-tooltip>

            <!-- View History -->
            <a-tooltip placement="bottom">
              <template slot="title">
                <span>{{ $t("BUTTONS.subscription_history") }}</span>
              </template>
              <button class="btn_show" @click="showHistory(item)">
                <i class="fal fa-history"></i>
              </button>
            </a-tooltip>

            <!-- Extend Subscription -->
            <a-tooltip placement="bottom">
              <template slot="title">
                <span>{{ $t("BUTTONS.extend_subscription") }}</span>
              </template>
              <span>
                <button
                  class="btn_edit"
                  @click="openExtendModalForItem(item)"
                  :disabled="item.status === 'cancelled'"
                >
                  <i class="fal fa-calendar-plus"></i>
                </button>
              </span>
            </a-tooltip>

            <!-- Deactivate Subscription -->
            <a-tooltip placement="bottom">
              <template slot="title">
                <span>{{ $t("BUTTONS.deactivate_subscription") }}</span>
              </template>
              <span>
                <button
                  class="btn_delete"
                  @click="openDeactivateModalForItem(item)"
                  :disabled="
                    item.status === 'cancelled' || item.status === 'expired'
                  "
                >
                  <i class="fal fa-ban"></i></button
              ></span>
            </a-tooltip>
          </div>
        </template>
        <!-- End:: Actions -->
      </v-data-table>
      <!--  =========== End:: Data Table =========== -->
    </main>
    <!-- End:: Main Section -->

    <!-- Start:: Pagination -->
    <template>
      <div class="pagination_container text-center mt-3 mb-0">
        <v-pagination
          class="py-0"
          square
          v-model="paginations.current_page"
          :length="paginations.last_page"
          :total-visible="6"
          @input="updateRouterQueryParam($event)"
          :prev-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-right' : 'fal fa-angle-left'
          "
          :next-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-left' : 'fal fa-angle-right'
          "
        />
      </div>
    </template>
    <!-- End:: Pagination -->

    <!-- Start:: Extend Subscription Modal -->
    <v-dialog v-model="extendModal" max-width="500px" persistent>
      <v-card>
        <div class="single_step_form_content_wrapper">
          <form>
            <v-card-title class="headline">
              {{ $t("TITLES.extend_subscription") }}
            </v-card-title>
            <v-card-text>
              <div class="row">
                <base-input
                  col="12"
                  type="number"
                  :placeholder="$t('PLACEHOLDERS.extension_days')"
                  v-model="extensionData.days"
                  required
                  min="1"
                />
                <base-input
                  col="12"
                  type="textarea"
                  :placeholder="$t('PLACEHOLDERS.extension_reason')"
                  v-model="extensionData.reason"
                  required
                  min="1"
                />
              </div>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="grey" text @click="closeExtendModal">
                {{ $t("BUTTONS.cancel") }}
              </v-btn>
              <v-btn
                color="success"
                @click="extendSubscription"
                :loading="isWaitingRequest"
                :disabled="!extensionData.days || !extensionData.reason"
              >
                {{ $t("BUTTONS.confirm") }}
              </v-btn>
            </v-card-actions>
          </form>
        </div>
      </v-card>
    </v-dialog>
    <!-- End:: Extend Subscription Modal -->

    <!-- Start:: Deactivate Subscription Modal -->
    <v-dialog v-model="deactivateModal" max-width="500px" persistent>
      <v-card>
        <div class="single_step_form_content_wrapper">
          <form>
            <v-card-title class="headline text-danger">
              {{ $t("TITLES.deactivate_subscription") }}
            </v-card-title>
            <v-card-text>
              <p class="mb-3">
                {{ $t("MESSAGES.deactivate_subscription_warning") }}
              </p>
              <div class="col-12">
                <base-input
                  type="textarea"
                  v-model="deactivationData.reason"
                  :placeholder="$t('PLACEHOLDERS.deactivation_reason')"
                  required
                />
              </div>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="grey" text @click="closeDeactivateModal">
                {{ $t("BUTTONS.cancel") }}
              </v-btn>
              <v-btn
                color="error"
                @click="deactivateSubscription"
                :loading="isWaitingRequest"
                :disabled="!deactivationData.reason"
              >
                {{ $t("BUTTONS.confirm") }}
              </v-btn>
            </v-card-actions>
          </form>
        </div>
      </v-card>
    </v-dialog>
    <!-- End:: Deactivate Subscription Modal -->
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "AllUserSubscriptions",

  computed: {
    ...mapGetters({
      getAppLocale: "AppLangModule/getAppLocale",
    }),
  },

  data() {
    return {
      // Start:: Loading Data
      loading: false,
      isWaitingRequest: false,
      // End:: Loading Data

      // Start:: Filter Data
      filterFormIsActive: false,
      filterOptions: {
        name: null,
        phone: null,
        status: null,
        start_date: null,
        end_date: null,
      },
      subscriptionStatuses: [
        {
          id: 1,
          value: "active",
          name: this.$t("PLACEHOLDERS.active_subscription"),
        },
        {
          id: 2,
          value: "expired",
          name: this.$t("PLACEHOLDERS.expired_subscription"),
        },
        {
          id: 3,
          value: "pending_renewal",
          name: this.$t("PLACEHOLDERS.pending_renewal"),
        },
        {
          id: 4,
          value: "inactivated_by_admin",
          name: this.$t("PLACEHOLDERS.cancelled_subscription"),
        },
      ],
      // End:: Filter Data

      // Start:: Table Data
      searchValue: "",
      tableHeaders: [
        {
          text: this.$t("PLACEHOLDERS.serial_number"),
          value: "id",
          align: "center",
          width: "80",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.user_id"),
          value: "user_id",
          align: "center",
          width: "100",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.full_name"),
          value: "full_name",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.identity_number"),
          value: "national_id",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.phone"),
          value: "mobile",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.email"),
          value: "email",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.subscription_start_date"),
          value: "start_date",
          align: "center",
          width: "140",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.subscription_end_date"),
          value: "end_date",
          align: "center",
          width: "140",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.remaining_days"),
          value: "remaining_days",
          align: "center",
          width: "120",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.subscription_status"),
          value: "status",
          align: "center",
          width: "140",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.subscription_price"),
          value: "price",
          align: "center",
          width: "120",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.last_payment_date"),
          value: "last_payment_date",
          align: "center",
          width: "140",
          sortable: false,
        },
        {
          text: this.$t("TABLES.Admins.actions"),
          value: "actions",
          sortable: false,
          align: "center",
          width: "100",
        },
      ],
      tableRows: [],
      // End:: Table Data

      // Start:: Pagination Data
      paginations: {
        current_page: 1,
        last_page: 1,
        items_per_page: 10,
      },
      // End:: Pagination Data

      // Start:: Modal Data
      extendModal: false,
      deactivateModal: false,
      selectedSubscriptionId: null,

      extensionData: {
        days: null,
        reason: null,
      },

      deactivationData: {
        reason: null,
      },
      // End:: Modal Data
    };
  },

  watch: {
    ["$route.query.page"]() {
      this.paginations.current_page = +this.$route.query.page;
      this.setTableRows();
    },
  },

  methods: {
    // Start:: Get Status Color
    getStatusColor(status) {
      switch (status) {
        case "active":
          return "success";
        case "expired":
          return "error";
        case "pending_renewal":
          return "warning";
        case "cancelled":
          return "grey";
        default:
          return "primary";
      }
    },
    // End:: Get Status Color

    // Start:: Get Status Text
    getStatusText(status) {
      switch (status) {
        case "active":
          return this.$t("PLACEHOLDERS.active_subscription");
        case "expired":
          return this.$t("PLACEHOLDERS.expired_subscription");
        case "inactivated_by_admin":
          return this.$t("PLACEHOLDERS.cancelled_subscription");
        case "pending_renewal":
          return this.$t("PLACEHOLDERS.pending_renewal");
        case "cancelled":
          return this.$t("PLACEHOLDERS.cancelled_subscription");
        default:
          return status;
      }
    },
    // End:: Get Status Text

    // Start:: Get Remaining Days Class
    getRemainingDaysClass(days) {
      if (days <= 3) return "text-danger font-weight-bold";
      if (days <= 7) return "text-warning font-weight-bold";
      return "text-success";
    },
    // End:: Get Remaining Days Class

    // Start:: Handel Filter
    async submitFilterForm() {
      if (this.$route.query.page !== "1") {
        await this.$router.push({
          path: "/user-subscriptions/all",
          query: { page: 1 },
        });
      }
      this.setTableRows();
    },
    async resetFilter() {
      this.filterOptions.name = null;
      this.filterOptions.phone = null;
      this.filterOptions.status = null;
      this.filterOptions.start_date = null;
      this.filterOptions.end_date = null;
      if (this.$route.query.page !== "1") {
        await this.$router.push({
          path: "/user-subscriptions/all",
          query: { page: 1 },
        });
      }
      this.setTableRows();
    },
    // End:: Handel Filter

    // Start:: Set Table Rows
    updateRouterQueryParam(pagenationValue) {
      this.$router.push({
        query: {
          ...this.$route.query,
          page: pagenationValue,
        },
      });
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    },
    async setTableRows() {
      this.loading = true;
      try {
        let res = await this.$axios({
          method: "GET",
          url: "subscriptions",
          params: {
            page: this.paginations.current_page,
            name: this.filterOptions.name,
            phone: this.filterOptions.phone,
            mobile: this.filterOptions.phone,
            status: this.filterOptions.status?.value,
            start_date: this.filterOptions.start_date,
            end_date: this.filterOptions.end_date,
          },
        });
        this.loading = false;

        this.tableRows = res.data.data.data.map((item) => ({
          id: item.id,
          user_id: item.user_id,
          full_name: item.full_name,
          national_id: item.national_id || "-",
          mobile: item.mobile,
          email: item.email,
          start_date: item.start_date ? item.start_date.split(" ")[0] : "-",
          end_date: item.end_date ? item.end_date.split(" ")[0] : "-",
          remaining_days: item.remaining_days,
          status: item.status,
          price: item.price,
          last_payment_date: item.last_payment_date
            ? item.last_payment_date.split("T")[0]
            : "-",
        }));

        this.paginations.last_page = res.data.data.meta.last_page;
        this.paginations.items_per_page = res.data.data.meta.per_page;
      } catch (error) {
        this.loading = false;
        console.log(error.response?.data?.message);
      }
    },
    // End:: Set Table Rows

    // Start:: Show Details
    showDetails(item) {
      this.$router.push({ path: `/user-subscriptions/show/${item.id}` });
    },
    // End:: Show Details

    // Start:: Show History
    showHistory(item) {
      this.$router.push({
        path: `/user-subscriptions/history/${item.user_id}`,
      });
    },
    // End:: Show History

    // Start:: Extend Modal Methods
    openExtendModalForItem(item) {
      this.selectedSubscriptionId = item.id;
      this.extendModal = true;
    },
    closeExtendModal() {
      this.extendModal = false;
      this.extensionData = { days: null, reason: null };
      this.selectedSubscriptionId = null;
    },
    async extendSubscription() {
      this.isWaitingRequest = true;
      const REQUEST_DATA = new FormData();
      REQUEST_DATA.append("extention_days", this.extensionData.days);
      REQUEST_DATA.append("extention_reason", this.extensionData.reason);

      try {
        await this.$axios({
          method: "POST",
          url: `subscriptions/extention/${this.selectedSubscriptionId}`,
          data: REQUEST_DATA,
        });
        this.isWaitingRequest = false;
        this.$message.success(
          this.$t("MESSAGES.subscription_extended_successfully")
        );
        this.closeExtendModal();
        this.setTableRows();
      } catch (error) {
        this.isWaitingRequest = false;
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.errorOccurred")
        );
      }
    },
    // End:: Extend Modal Methods

    // Start:: Deactivate Modal Methods
    openDeactivateModalForItem(item) {
      this.selectedSubscriptionId = item.id;
      this.deactivateModal = true;
    },
    closeDeactivateModal() {
      this.deactivateModal = false;
      this.deactivationData = { reason: null };
      this.selectedSubscriptionId = null;
    },
    async deactivateSubscription() {
      this.isWaitingRequest = true;
      const REQUEST_DATA = new FormData();
      REQUEST_DATA.append("deactivation_reason", this.deactivationData.reason);

      try {
        await this.$axios({
          method: "POST",
          url: `subscriptions/deactivate/${this.selectedSubscriptionId}`,
          data: REQUEST_DATA,
        });
        this.isWaitingRequest = false;
        this.$message.success(
          this.$t("MESSAGES.subscription_deactivated_successfully")
        );
        this.closeDeactivateModal();
        this.setTableRows();
      } catch (error) {
        this.isWaitingRequest = false;
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.errorOccurred")
        );
      }
    },
    // End:: Deactivate Modal Methods
  },

  created() {
    window.addEventListener("click", () => {
      this.filterFormIsActive = false;
    });
    if (this.$route.query.page) {
      this.paginations.current_page = +this.$route.query.page;
    }
    this.setTableRows();
  },
};
</script>
